import { CUSTOM_URL, FUSION_PREFIX, TEAM_PREFIX } from 'services/_infra/environment';
import { DOPPLER_CONFIG } from '../config/dopplerRadar.config.js';
import { DOPPLER_TILE_SOURCE_MODES } from './dopplerTileSource.utils.js';

const getFusionBaseUrl = () => `${CUSTOM_URL}${TEAM_PREFIX}${FUSION_PREFIX}`;
const CW_XYZ_TYPES = new Set(DOPPLER_CONFIG.customWeatherXyzTypes);

let hasWarnedAboutFallback = false;
let hasLoggedDirectMode = false;
let hasLoggedCircuitBreakerFallback = false;

export function xyzToQuadkey(x, y, z) {
  const quadKey = [];

  for (let i = z; i > 0; i -= 1) {
    let digit = 0;
    const mask = 1 << (i - 1);
    if ((x & mask) !== 0) {
      digit += 1;
    }
    if ((y & mask) !== 0) {
      digit += 2;
    }
    quadKey.push(String(digit));
  }

  return quadKey.join('');
}

const normalizeBaseUrl = (baseUrl) =>
  String(baseUrl ?? '')
    .trim()
    .replace(/\/+$/, '');

const getCustomWeatherBaseUrl = () => {
  const normalizedBaseUrl = normalizeBaseUrl(DOPPLER_CONFIG.customWeatherBaseUrl);
  if (!normalizedBaseUrl) {
    return '';
  }

  if (
    typeof window !== 'undefined' &&
    window.location.protocol === 'https:' &&
    normalizedBaseUrl.startsWith('http://')
  ) {
    return '';
  }

  return normalizedBaseUrl;
};

export const canUseDirectCustomWeatherTiles = () =>
  Boolean(getCustomWeatherBaseUrl() && DOPPLER_CONFIG.customWeatherToken);

export const canUseFusionProxyTiles = () => DOPPLER_CONFIG.customWeatherProxyFallbackEnabled;

const canUseTileSource = (tileSourceMode) => {
  if (tileSourceMode === DOPPLER_TILE_SOURCE_MODES.direct) {
    return canUseDirectCustomWeatherTiles();
  }

  if (tileSourceMode === DOPPLER_TILE_SOURCE_MODES.proxy) {
    return canUseFusionProxyTiles();
  }

  return false;
};

export function resolveTileSourceMode(tileSourceMode) {
  const preferredTileSources = [
    ...(tileSourceMode ? [tileSourceMode] : []),
    DOPPLER_TILE_SOURCE_MODES.direct,
    DOPPLER_TILE_SOURCE_MODES.proxy,
  ];

  return preferredTileSources.find(canUseTileSource) ?? null;
}

const resolveDopplerLayerType = (baseTime, frameTime, explicitLayer) => {
  if (explicitLayer !== undefined && explicitLayer !== null && explicitLayer !== '') {
    return explicitLayer;
  }

  const leadMinutes = Math.max(0, (frameTime.getTime() - baseTime.getTime()) / (1000 * 60));

  return leadMinutes >= DOPPLER_CONFIG.futureRadarStartMinutes
    ? DOPPLER_CONFIG.futureRadarLayerType
    : DOPPLER_CONFIG.currentRadarLayerType;
};

const buildCustomWeatherTileUrl = ({ baseTime, frameTime, layer, x, y, z }) => {
  const baseUrl = getCustomWeatherBaseUrl();
  const currentDate = baseTime.toISOString();
  const futureDate = frameTime.toISOString();
  const token = DOPPLER_CONFIG.customWeatherToken;

  if (CW_XYZ_TYPES.has(layer)) {
    return `${baseUrl}/${token}/${currentDate}/${futureDate}/${layer}/${z}/${x}/${y}`;
  }

  return `${baseUrl}/${token}/${currentDate}/${futureDate}/${layer}/{quadkey}.png`;
};

const buildFusionProxyTileUrl = (baseTime, frameTime, layer) => {
  const fusionBaseUrl = getFusionBaseUrl();
  return `${fusionBaseUrl}v2/alert/map-tile/${baseTime.toISOString()}/${frameTime.toISOString()}/${layer}/{z}/{x}/{y}`;
};

const logFallbackReason = (reason) => {
  if (hasWarnedAboutFallback || process.env.NODE_ENV === 'production') {
    return;
  }

  hasWarnedAboutFallback = true;
  console.info(`[Doppler] Using backend tile proxy fallback: ${reason}`);
};

const logDirectMode = (baseUrl) => {
  if (hasLoggedDirectMode || process.env.NODE_ENV === 'production') {
    return;
  }

  hasLoggedDirectMode = true;
  console.info(`[Doppler] Using direct CustomWeather tiles: ${baseUrl}`);
};

const getDirectTilesUnavailableReason = () => {
  if (!DOPPLER_CONFIG.customWeatherToken) {
    return 'CustomWeather tile token is not configured.';
  }

  if (!getCustomWeatherBaseUrl()) {
    return 'CustomWeather tile base URL is missing or blocked under HTTPS.';
  }

  return null;
};

const logDirectTilesUnavailable = () => {
  const reason = getDirectTilesUnavailableReason();
  if (reason) {
    logFallbackReason(reason);
  }
};

export function logDirectTileFallbackSwitch(reason) {
  if (hasLoggedCircuitBreakerFallback || process.env.NODE_ENV === 'production') {
    return;
  }

  hasLoggedCircuitBreakerFallback = true;
  console.warn(`[Doppler] Switching to backend tile proxy after direct tile errors: ${reason}`);
}

export function getMapTileUrl(baseTime, frameTime, layer, { tileSourceMode } = {}) {
  const resolvedLayer = resolveDopplerLayerType(baseTime, frameTime, layer);
  const resolvedTileSourceMode = resolveTileSourceMode(tileSourceMode);

  if (resolvedTileSourceMode === DOPPLER_TILE_SOURCE_MODES.direct) {
    logDirectMode(getCustomWeatherBaseUrl());
    return buildCustomWeatherTileUrl({
      baseTime,
      frameTime,
      layer: resolvedLayer,
      x: '{x}',
      y: '{y}',
      z: '{z}',
    });
  }

  if (resolvedTileSourceMode === DOPPLER_TILE_SOURCE_MODES.proxy) {
    if (tileSourceMode !== DOPPLER_TILE_SOURCE_MODES.proxy) {
      logDirectTilesUnavailable();
    }

    return buildFusionProxyTileUrl(baseTime, frameTime, resolvedLayer);
  }

  logDirectTilesUnavailable();
  return '';
}

export function isQuadkeyTileUrl(url) {
  return typeof url === 'string' && url.includes('{quadkey}');
}
